import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useClickAway } from "react-use";
import { useMedia } from "react-use";

import { DEFAULT_ANIMATION_DURATION_MILLISECONDS } from "@oneleet/config/constants";

import * as Portal from "@radix-ui/react-portal";
import { AnimatePresence } from "framer-motion";

import { cn } from "../../tailwind";
import {
  DEFAULT_DURATION_FOR_TITLE,
  DEFAULT_DURATION_FOR_TITLE_AND_DESCRIPTION,
  DEFAULT_ERROR_DESCRIPTION,
  SLIDE_ANIMATION_DISTANCE,
} from "./constants";
import { NotificationContext } from "./hook";
import type { Notification, NotifyInput } from "./types";
import { Toast } from "@oneleet/components/src/molecules";

export function NotificationProvider({ children }: { children?: ReactNode }) {
  const ref = useRef(null);
  const isMobile = useMedia("(max-width: 640px)");
  const [currentNotification, setCurrentNotification] =
    useState<Notification | null>(null);

  const dismiss = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  const notify = useCallback(
    (notification: NotifyInput) => {
      if (currentNotification) {
        setCurrentNotification(null);
      }

      const isDismissableDefault = notification.type !== "success";
      const shouldDismissAutomaticallyDefault = notification.type === "success";
      const shouldDismissOnClickAwayDefault = notification.type === "error";

      if (
        notification.type === "error" &&
        notification.showDefaultErrorDescription
      ) {
        notification.description = DEFAULT_ERROR_DESCRIPTION;
      }

      let duration = notification.duration;
      if (duration === undefined) {
        duration =
          notification.description || notification.content
            ? DEFAULT_DURATION_FOR_TITLE_AND_DESCRIPTION
            : DEFAULT_DURATION_FOR_TITLE;
      }

      setTimeout(() => {
        setCurrentNotification({
          ...notification,
          duration,
          isDismissable: notification.isDismissable ?? isDismissableDefault,
          shouldDismissAutomatically:
            notification.shouldDismissAutomatically ??
            shouldDismissAutomaticallyDefault,
          shouldDismissOnClickAway:
            notification.shouldDismissOnClickAway ??
            shouldDismissOnClickAwayDefault,
        });
      }, DEFAULT_ANIMATION_DURATION_MILLISECONDS);

      return { dismiss };
    },
    [currentNotification, setCurrentNotification, dismiss],
  );

  useClickAway(ref, () => {
    if (currentNotification?.shouldDismissOnClickAway) {
      setCurrentNotification(null);
    }
  }, ["mouseup"]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (currentNotification?.shouldDismissAutomatically) {
      timer = setTimeout(() => {
        setCurrentNotification(null);
      }, currentNotification.duration);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [currentNotification, setCurrentNotification]);

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <AnimatePresence>
        {currentNotification && (
          <Portal.Root>
            <div
              className={cn(
                "pointer-events-none fixed inset-0 z-[70] flex w-full items-end px-4 py-6 sm:p-6",
                currentNotification.notifyContainerClassName,
              )}
            >
              <div className="pointer-events-none flex w-full flex-col items-center space-y-4 sm:items-end">
                <Toast
                  title={currentNotification.title}
                  description={currentNotification.description}
                  content={currentNotification.content}
                  type={currentNotification.type}
                  dismissable={currentNotification.isDismissable}
                  initial={{
                    x: isMobile ? 0 : SLIDE_ANIMATION_DISTANCE,
                    y: isMobile ? SLIDE_ANIMATION_DISTANCE : 0,
                    opacity: 0,
                  }}
                  animate={{ x: 0, y: 0, opacity: 1 }}
                  exit={{
                    x: isMobile ? 0 : SLIDE_ANIMATION_DISTANCE,
                    y: isMobile ? SLIDE_ANIMATION_DISTANCE : 0,
                    opacity: 0,
                  }}
                  className="pointer-events-auto"
                  onDismiss={dismiss}
                  ref={ref}
                />
              </div>
            </div>
          </Portal.Root>
        )}
      </AnimatePresence>
    </NotificationContext.Provider>
  );
}
